// Business page: this business's Gems from THE HOARD (stone + photo + what you get), the Gem rules, the counter
// redeem flow and the owner's highlights. Before the HOARD opens, visitors see a "coming" card; the owner sees everything.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Gem, Ticket, Hand, CalendarRange, ArrowRight } from "lucide-react";
import BlockShell from "./BlockShell";
import { GemSvg } from "../hoard/GemSvg";
import { GemRedeemPanel } from "../hoard/GemRedeemPanel";
import { BusinessHighlights } from "../hoard/Highlights";
import { API } from "../../App";

export const GEM_SEASON = "Sept 21, 2026 – Sept 21, 2027";
const abs = (u) => (u?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const RULES = [
  { icon: Ticket, title: "Give, unlock your HOARD", body: "Give to a local cause on THE WALL — your receipt unlocks your HOARD." },
  { icon: Hand, title: "Staff tap REDEEM", body: "Open the Gem at the counter and hand staff your phone. Each Gem works once per HOARD." },
  { icon: CalendarRange, title: "One full season", body: `This season's Gems run ${GEM_SEASON}.` },
];

const GemCard = ({ g }) => (
  <li className="flex gap-4 rounded-2xl bg-white border border-[#E8E2D4] p-4 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow]" data-testid={`business-gem-${g.item_id}`}>
    {g.photo_url
      ? <img src={abs(g.photo_url)} alt="" className="h-20 w-20 rounded-xl object-cover shrink-0" />
      : <span className="h-20 w-20 rounded-xl bg-[#FBF7EE] flex items-center justify-center shrink-0"><GemSvg cut={g.cut} color={g.color} size={52} title={g.stone} /></span>}
    <div className="min-w-0 flex-1">
      <p className="m-0 text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: g.color }}>{g.stone} · {g.label}</p>
      <p className="m-0 mt-0.5 font-['Playfair_Display'] text-lg font-semibold text-[#1A1A1A] leading-snug">{g.title}</p>
      {g.details && <p className="m-0 mt-1 text-sm text-[#5C5C5C] leading-relaxed">{g.details}</p>}
      {g.preview && <p className="m-0 mt-2 inline-block rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid="business-gem-preview">In review — only you can see this</p>}
    </div>
  </li>
);

const BusinessGemsBlock = ({ block }) => {
  const cfg = block.config || {};
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!cfg.business_id) return;
    axios.get(`${API}/businesses/${cfg.business_id}/gems`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [cfg.business_id]);
  if (!data) return null;
  const opens = new Date(data.live_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  return (
    <>
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10" data-testid="business-gems-block">
        <BlockShell block={block} accent={cfg.accent || "#B8860B"}>
          {data.gems.length ? (
            <ul className="m-0 p-0 list-none grid gap-4 md:grid-cols-2" data-testid="business-gems-list">{data.gems.map((g) => <GemCard key={g.item_id} g={g} />)}</ul>
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-[#C9A24A] bg-[#FBF7EE] p-6 flex items-center gap-4" data-testid="business-gems-coming">
              <Gem className="h-9 w-9 text-[#B8860B] shrink-0" />
              <p className="m-0 text-[#1A1A1A]">
                {data.live ? "New Gems are on the way — check back soon." : <>Our Gems open in THE WALL HOARD on <b>{opens}</b>. Unlock your HOARD ahead of time.</>}
              </p>
            </div>
          )}
          <div className="mt-6 rounded-2xl bg-[#0A1626] text-white p-5 sm:p-6" data-testid="business-gem-rules">
            <p className="m-0 text-[10px] font-black uppercase tracking-[0.25em] text-[#F2CC8F]">How Gems work</p>
            <ol className="m-0 mt-3 p-0 list-none grid gap-4 sm:grid-cols-3">
              {RULES.map(({ icon: Icon, title, body }, i) => (
                <li key={title} className="flex gap-3">
                  <span className="h-9 w-9 shrink-0 rounded-xl bg-[#F2CC8F] text-[#0A1626] flex items-center justify-center"><Icon className="h-4 w-4" /></span>
                  <span><span className="block font-bold text-sm">{i + 1}. {title}</span><span className="block text-xs text-white/75 leading-relaxed mt-0.5">{body}</span></span>
                </li>
              ))}
            </ol>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/passport" className="inline-flex items-center gap-1.5 rounded-full bg-[#F2CC8F] text-[#0A1626] px-4 py-2 text-xs font-black uppercase tracking-wider hover:bg-white transition-colors" data-testid="business-gems-passport">Unlock your HOARD <ArrowRight className="h-3.5 w-3.5" /></Link>
              <Link to="/gem-demo" className="inline-flex items-center rounded-full border border-white/30 px-4 py-2 text-xs font-bold hover:bg-white/10 transition-colors" data-testid="business-gems-demo">Watch how it works</Link>
            </div>
          </div>
          {cfg.show_highlights !== false && <div className="mt-6"><BusinessHighlights business={{ business_id: data.business_id, name: data.name, highlights: data.highlights }} className="" /></div>}
        </BlockShell>
      </section>
      {data.live && <GemRedeemPanel businessId={data.business_id} businessName={data.name} />}
    </>
  );
};

export default BusinessGemsBlock;
